import { Icon } from "@indigo-components";
import { useEffect, useState } from "react";
import { useAuthSession } from "~/auth/AuthSessionProvider";

/**
 * Shown when the user's session has expired but we've kept them on their current
 * page (see AuthSessionProvider's grace path). Dismissable; opening sign-in in a
 * new tab lets them re-authenticate and keep this tab's context — once the
 * fresh tokens are picked up (on tab re-focus), the session recovers and this
 * banner disappears on its own.
 */
const SessionExpiredBanner = () => {
  const { sessionExpired } = useAuthSession();
  const [dismissed, setDismissed] = useState(false);

  // Re-show on each new expiry so a prior dismissal doesn't hide a later one.
  useEffect(() => {
    if (sessionExpired) setDismissed(false);
  }, [sessionExpired]);

  if (!sessionExpired || dismissed) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex items-center gap-3 bg-rose-600 dark:bg-rose-400 px-4 py-2 text-indigo-text-on-accent shadow-sm"
    >
      <Icon name="functional-warning-reversed" size="s" />
      <span className="flex-1 text-xs font-medium">
        Your session has expired. Your work is still here — sign in again to
        keep saving. You can sign in on a new tab and come back to this one.
      </span>
      <button
        type="button"
        onClick={() => window.open("/login", "_blank", "noopener")}
        className="rounded bg-white/15 px-2.5 py-1 text-xs font-semibold hover:bg-white/25"
      >
        Sign in
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss session expired warning"
        className="rounded p-1 text-white/90 hover:bg-white/10 hover:text-indigo-text-on-accent"
      >
        <Icon name="functional-close" size="s" />
      </button>
    </div>
  );
};

export default SessionExpiredBanner;
